/**
 * @fileoverview Modal responsável por criar e editar usuários a partir da página de usuários.
 */

import React, { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import InputMask from "react-input-mask";
import {
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  MenuItem,
  Avatar,
  Typography,
  Box,
  Switch,
  Collapse,
} from "@material-ui/core";
import {
  Plus,
  Pencil,
  X,
  Upload,
  User as PersonIcon,
  Sparkles as AIIcon,
  ChevronDown,
  ShieldCheck,
  ShieldOff,
  RotateCcw,
  Headset,
  Clock,
  Info,
} from "lucide-react";
import CircularProgress from "@material-ui/core/CircularProgress";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useOnboarding } from "../../context/Onboarding/OnboardingContext";
import ModernSelect from "../ModernSelect";
import QueueMultiSelectPopover from "../QueueMultiSelectPopover";
import UserScheduleField, {
  scheduleValueToGroups,
  scheduleGroupsToValue,
  scheduleGroupsHaveErrors,
} from "../UserScheduleField";

const MENU_PROPS = {
  disableScrollLock: true,
  MenuListProps: { dense: true },
};

// Chat Interno "travado": conta paga sem assentos internos disponíveis
// (internos_used >= internos_allowed). Fora de pago_ativo ou sem teto = liberado.
// Espelha a regra do backend (CreateUserService/UpdateUserService).
const isInternalLocked = (billing) =>
  billing?.status === "pago_ativo" &&
  billing?.internos_allowed != null &&
  typeof billing?.seats_internal === "number" &&
  billing.seats_internal >= billing.internos_allowed;

const getInitialFormData = () => ({
  name: "",
  email: "",
  cpf: "",
  password: "",
  profile: "user",
  queueIds: [],
  whatsappId: "",
  allTicket: "disabled",
  profileImage: "",
  jobRoleId: "",
  isAttendant: true,
  accessScope: "full",
  isAI: false,
  aiEnabled: true,
  aiWebhookUrl: "",
  aiActions: {
    canTransferUser: true,
    canTransferQueue: true,
    canResolveTicket: true,
    canSendMessage: true,
  },
  aiQueueIds: [],
  aiAnswerNoQueue: true,
  aiWhatsappIds: [],
});

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: "24px",
      width: "min(780px, calc(100vw - 32px))",
      maxWidth: "calc(100vw - 32px)",
      background:
        theme.mode === "dark"
          ? "linear-gradient(135deg, rgba(18, 18, 19, 0.98) 0%, rgba(22, 22, 24, 0.98) 100%)"
          : "#FFFFFF",
      boxShadow:
        theme.mode === "dark"
          ? "0 8px 24px rgba(0, 0, 0, 0.5)"
          : "0 8px 24px rgba(0, 0, 0, 0.1)",
      border: theme.mode === "dark" ? "1px solid rgba(91, 124, 255, 0.2)" : "none",
      [theme.breakpoints.down("sm")]: {
        minWidth: "unset",
        width: "calc(100% - 16px)",
        margin: 8,
        borderRadius: "16px",
      },
    },
    "& .MuiBackdrop-root": {
      backgroundColor:
        theme.mode === "dark" ? "rgba(0, 0, 0, 0.92)" : "rgba(15, 23, 42, 0.6)",
    },
  },
  dialogTitle: {
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom:
      theme.mode === "dark"
        ? "1px solid rgba(255, 255, 255, 0.08)"
        : "1px solid rgba(15, 23, 42, 0.08)",
    "& .MuiTypography-root": {
      fontSize: "1.35rem",
      fontWeight: 700,
      letterSpacing: "-0.01em",
      color: theme.mode === "dark" ? "#f8fafc" : "#0f172a",
    },
  },
  formControl: {
    marginBottom: theme.spacing(2),
    width: "100%",
  },
  formField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      background:
        theme.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
      border:
        theme.mode === "dark"
          ? "1px solid rgba(91, 124, 255, 0.15)"
          : "1px solid rgba(0, 0, 0, 0.08)",
      transition: "background-color 0.2s ease, border-color 0.2s ease",
      "& fieldset": {
        borderColor: "transparent",
      },
      "&:hover": {
        background:
          theme.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(61, 92, 253, 0.04)",
        border:
          theme.mode === "dark"
            ? "1px solid rgba(91, 124, 255, 0.25)"
            : "1px solid rgba(61, 92, 253, 0.15)",
      },
      "&.Mui-focused": {
        background:
          theme.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(255, 255, 255, 1)",
        border:
          theme.mode === "dark"
            ? "1px solid rgba(91, 124, 255, 0.4)"
            : "1px solid rgba(61, 92, 253, 0.3)",
        boxShadow: "0 0 0 2px rgba(91, 124, 255, 0.25)",
      },
    },
    // Altura dos inputs: MUI usa 18.5px de padding vertical (fica "gordo").
    // Ajuste aqui (ex.: 10 = mais fino, 14 = mais alto).
    "& .MuiOutlinedInput-input": {
      paddingTop: 12,
      paddingBottom: 12,
    },
    // Recentraliza o label "em repouso" (campo vazio) para a nova altura — o MUI
    // assume 18.5px e usa translateY(20px), que fica baixo demais aqui.
    "& .MuiInputLabel-outlined": {
      transform: "translate(14px, 14px) scale(1)",
    },
    // Mantém a posição flutuada (com valor/foco) no topo da borda.
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.75)",
    },
  },
  uploadButton: {
    marginTop: theme.spacing(1),
    borderRadius: "12px",
    textTransform: "none",
    fontWeight: 600,
  },
  mfaCard: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 14,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    background:
      theme.mode === "dark"
        ? "rgba(91, 124, 255, 0.06)"
        : "rgba(61, 92, 253, 0.04)",
    border:
      theme.mode === "dark"
        ? "1px solid rgba(91, 124, 255, 0.18)"
        : "1px solid rgba(61, 92, 253, 0.12)",
  },
  mfaCardLeft: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  mfaIconBadge: {
    width: 38,
    height: 38,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  mfaIconBadgeOn: {
    background:
      theme.mode === "dark"
        ? "rgba(16, 185, 129, 0.18)"
        : "rgba(16, 185, 129, 0.12)",
    color: theme.mode === "dark" ? "#34d399" : "#059669",
    border:
      theme.mode === "dark"
        ? "1px solid rgba(16, 185, 129, 0.35)"
        : "1px solid rgba(16, 185, 129, 0.25)",
  },
  mfaIconBadgeOff: {
    background:
      theme.mode === "dark"
        ? "rgba(255, 255, 255, 0.04)"
        : "rgba(0, 0, 0, 0.04)",
    color: theme.mode === "dark" ? "rgba(255, 255, 255, 0.45)" : "#94a3b8",
    border:
      theme.mode === "dark"
        ? "1px solid rgba(255, 255, 255, 0.08)"
        : "1px solid rgba(0, 0, 0, 0.06)",
  },
  mfaTextWrap: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  mfaTitle: {
    fontSize: "0.85rem",
    fontWeight: 600,
    color: theme.mode === "dark" ? "#f1f5f9" : "#1e293b",
    lineHeight: 1.2,
  },
  mfaStatus: {
    fontSize: "0.75rem",
    fontWeight: 500,
    marginTop: 2,
  },
  mfaStatusOn: {
    color: theme.mode === "dark" ? "#34d399" : "#059669",
  },
  mfaStatusOff: {
    color: theme.mode === "dark" ? "rgba(255, 255, 255, 0.5)" : "#94a3b8",
  },
  mfaResetBtn: {
    fontSize: "0.72rem",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 10,
    padding: "6px 12px",
    whiteSpace: "nowrap",
    color: theme.mode === "dark" ? "#fca5a5" : "#dc2626",
    borderColor:
      theme.mode === "dark"
        ? "rgba(220, 38, 38, 0.35)"
        : "rgba(220, 38, 38, 0.3)",
    "&:hover": {
      background:
        theme.mode === "dark"
          ? "rgba(220, 38, 38, 0.1)"
          : "rgba(220, 38, 38, 0.06)",
      borderColor: theme.mode === "dark" ? "#fca5a5" : "#dc2626",
    },
  },
  imagePreview: {
    marginTop: theme.spacing(2),
    textAlign: "center",
  },
  previewAvatar: {
    width: 120,
    height: 120,
    margin: "0 auto",
  },
  userTypeSelector: {
    display: "flex",
    background: theme.mode === "dark" ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
    borderRadius: "16px",
    padding: "4px",
    marginBottom: theme.spacing(3),
    border:
      theme.mode === "dark"
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(0, 0, 0, 0.06)",
  },
  userTypeButton: {
    flex: 1,
    padding: theme.spacing(1.5, 2),
    borderRadius: "12px",
    cursor: "pointer",
    transition: "background-color 0.2s ease, box-shadow 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    background: "transparent",
    border: "none",
    "&:hover": {
      background: theme.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.03)",
    },
  },
  userTypeButtonActive: {
    background: theme.mode === "dark" ? "#5B7CFF" : "#3D5CFD",
    "& $userTypeLabel": {
      color: "#FFFFFF",
    },
    "& $userTypeIcon": {
      color: "#FFFFFF",
    },
    "&:hover": {
      background: theme.mode === "dark" ? "#4A67FD" : "#2A4BDB",
    },
  },
  userTypeIcon: {
    fontSize: 22,
    color: theme.mode === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.4)",
    transition: "color 0.3s ease",
  },
  userTypeLabel: {
    fontWeight: 600,
    fontSize: "0.9rem",
    color: theme.mode === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)",
    transition: "color 0.3s ease",
  },
  aiSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2.5),
    background:
      theme.mode === "dark"
        ? "linear-gradient(135deg, rgba(91, 124, 255, 0.08) 0%, rgba(157, 124, 255, 0.08) 100%)"
        : "linear-gradient(135deg, rgba(61, 92, 253, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%)",
    borderRadius: "16px",
    border:
      theme.mode === "dark"
        ? "1px solid rgba(91, 124, 255, 0.15)"
        : "1px solid rgba(61, 92, 253, 0.1)",
  },
  switchContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1, 0),
  },
  codeBlock: {
    background: theme.mode === "dark" ? "rgba(0, 0, 0, 0.3)" : "rgba(0, 0, 0, 0.04)",
    borderRadius: "12px",
    padding: theme.spacing(2),
    fontFamily: '"Fira Code", "Monaco", monospace',
    fontSize: "0.75rem",
    overflowX: "auto",
    border:
      theme.mode === "dark"
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(0, 0, 0, 0.06)",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    "& pre": {
      margin: 0,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    },
  },
  docSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    background: theme.mode === "dark" ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.02)",
    borderRadius: "12px",
    border:
      theme.mode === "dark"
        ? "1px solid rgba(255, 255, 255, 0.05)"
        : "1px solid rgba(0, 0, 0, 0.05)",
  },
  docTitle: {
    fontWeight: 600,
    fontSize: "0.85rem",
    marginBottom: theme.spacing(1),
    color: theme.mode === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    cursor: "pointer",
    "&:hover": {
      color: theme.mode === "dark" ? "#5B7CFF" : "#3D5CFD",
    },
  },
  gradientText: {
    background: "linear-gradient(135deg, #5B7CFF 0%, #9D7CFF 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    fontWeight: 700,
  },
  loadingState: {
    padding: theme.spacing(6, 2),
    textAlign: "center",
    color: theme.mode === "dark" ? "rgba(255, 255, 255, 0.55)" : "#64748b",
  },

  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: theme.palette.type === "dark"
      ? "1px solid rgba(255, 255, 255, 0.06)"
      : "1px solid rgba(15, 23, 42, 0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
  btnBase: {
    height: 38,
    padding: "0 16px",
    borderRadius: 10,
    fontSize: "0.875rem",
    fontWeight: 500,
    textTransform: "none",
    whiteSpace: "nowrap",
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    border: "none",
    boxShadow: "none",
    transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
    "& svg": { flexShrink: 0 },
    "& .MuiButton-label": { whiteSpace: "nowrap" },
  },
  btnCancel: {
    background: "transparent",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255, 255, 255, 0.12)"
      : "1px solid rgba(15, 23, 42, 0.12)",
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
    "&:hover": {
      background: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.04)",
      color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
    },
  },
  btnPrimary: {
    background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
    color: "#ffffff",
    "&:hover": {
      background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
    },
    "&:disabled": { opacity: 0.55, cursor: "not-allowed" },
  },
  spinner: { color: "#ffffff" },

  /* ── Barra de abas (minimalista: ativo = fundo sutil + borda azul) ── */
  tabBar: {
    display: "flex",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(3),
  },
  tabBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    height: 42,
    padding: "0 18px",
    borderRadius: 11,
    border: "1px solid transparent",
    background: "transparent",
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "0.875rem",
    fontWeight: 500,
    whiteSpace: "nowrap",
    color: theme.mode === "dark" ? "rgba(255, 255, 255, 0.5)" : "rgba(15, 23, 42, 0.5)",
    transition: "color .18s ease, background .18s ease, border-color .18s ease",
    "& svg": { flexShrink: 0, opacity: 0.85 },
    "&:hover": {
      color: theme.mode === "dark" ? "rgba(255, 255, 255, 0.85)" : "rgba(15, 23, 42, 0.8)",
      background: theme.mode === "dark" ? "rgba(255, 255, 255, 0.03)" : "rgba(15, 23, 42, 0.03)",
    },
    [theme.breakpoints.down("xs")]: { flex: 1, padding: "0 10px", gap: 6 },
  },
  tabBtnActive: {
    color: theme.mode === "dark" ? "#ffffff" : "#0f172a",
    background: theme.mode === "dark" ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.025)",
    border:
      theme.mode === "dark"
        ? "1px solid rgba(91, 124, 255, 0.65)"
        : "1px solid rgba(61, 92, 253, 0.55)",
    "& svg": { opacity: 1, color: theme.mode === "dark" ? "#7c9bff" : "#3D5CFD" },
    "&:hover": {
      color: theme.mode === "dark" ? "#ffffff" : "#0f172a",
      background: theme.mode === "dark" ? "rgba(255, 255, 255, 0.055)" : "rgba(15, 23, 42, 0.03)",
    },
  },
  tabLabelXs: {
    [theme.breakpoints.down("xs")]: { display: "none" },
  },

  /* ── Painel da aba (com fade) ── */
  "@keyframes panelFade": {
    from: { opacity: 0, transform: "translateY(5px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  tabPanel: {
    animation: "$panelFade 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
    minHeight: 296,
  },

  /* ── Linha de 2 colunas ── */
  row2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
  },
  formControlTight: {
    marginBottom: 0,
    width: "100%",
  },

  /* ── Cabeçalho de identidade (avatar + nome) ── */
  identityRow: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    marginBottom: theme.spacing(2.5),
  },
  avatarUpload: {
    position: "relative",
    width: 84,
    height: 84,
    borderRadius: "50%",
    overflow: "hidden",
    cursor: "pointer",
    flexShrink: 0,
    border:
      theme.mode === "dark"
        ? "1px solid rgba(91, 124, 255, 0.3)"
        : "1px solid rgba(0, 0, 0, 0.08)",
    "&:hover $avatarOverlay": { opacity: 1 },
  },
  identityAvatar: {
    width: 84,
    height: 84,
    fontSize: 30,
    fontWeight: 700,
    background: theme.mode === "dark" ? "rgba(91, 124, 255, 0.16)" : "rgba(61, 92, 253, 0.08)",
    color: theme.mode === "dark" ? "#9db4ff" : "#3D5CFD",
  },
  avatarOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 2,
    background: "rgba(10, 12, 20, 0.55)",
    color: "#ffffff",
    opacity: 0,
    transition: "opacity .2s ease",
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  identityFields: { flex: 1, minWidth: 0 },
  identityHint: {
    display: "block",
    marginTop: 6,
    fontSize: 11.5,
    color: theme.mode === "dark" ? "rgba(255, 255, 255, 0.4)" : "#94a3b8",
  },

  /* ── Card de aviso (aba Segurança em criação) ── */
  hintCard: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    borderRadius: 14,
    background: theme.mode === "dark" ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.02)",
    border:
      theme.mode === "dark"
        ? "1px dashed rgba(255, 255, 255, 0.12)"
        : "1px dashed rgba(15, 23, 42, 0.12)",
    color: theme.mode === "dark" ? "rgba(255, 255, 255, 0.62)" : "#64748b",
    fontSize: "0.85rem",
    lineHeight: 1.5,
  },

  /* ── Campo com trigger de popover (rótulo acima) ── */
  pickerField: {
    marginBottom: theme.spacing(2),
  },
  pickerLabel: {
    display: "block",
    marginBottom: 7,
    marginLeft: 2,
    fontSize: 12,
    fontWeight: 500,
    color: theme.mode === "dark" ? "#94a3b8" : "#64748b",
  },

  /* ── Linha de switch (boxed) ── */
  switchRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1.75),
    borderRadius: 12,
    marginBottom: theme.spacing(2),
    background: theme.mode === "dark" ? "rgba(255, 255, 255, 0.03)" : "rgba(248, 250, 252, 0.9)",
    border:
      theme.mode === "dark"
        ? "1px solid rgba(255, 255, 255, 0.08)"
        : "1px solid rgba(15, 23, 42, 0.08)",
  },
  switchRowLabel: {
    fontSize: "0.9rem",
    fontWeight: 500,
    color: theme.mode === "dark" ? "#f1f5f9" : "#1e293b",
  },
}));

const UserEditorModal = ({ open, onClose, onSaved, selectedUser }) => {
  const classes = useStyles();
  const { user: loggedInUser } = useContext(AuthContext);
  const { billing } = useOnboarding();
  // Sem assentos de Chat Interno → trava as opções com escopo interno no seletor.
  const internalLocked = isInternalLocked(billing);

  const [formData, setFormData] = useState(getInitialFormData());
  const [imagePreview, setImagePreview] = useState("");
  const [queues, setQueues] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [userCompanyIds, setUserCompanyIds] = useState([]);
  const [userMaxCompanies, setUserMaxCompanies] = useState(1);
  const [showApiDocs, setShowApiDocs] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("dados");
  // Editor de Horários: faixas de expediente do atendente. Vive AQUI (não no
  // formData) p/ sobreviver à troca de abas (inclusive faixas pela metade);
  // é achatado para `workingHours` só no save.
  const [scheduleGroups, setScheduleGroups] = useState([]);
  // Após uma tentativa de salvar com horário inválido, a aba passa a
  // exibir os erros de todos os campos (antes, só os já tocados).
  const [scheduleShowErrors, setScheduleShowErrors] = useState(false);

  const updateFormData = (patch) => {
    setFormData((prevState) => ({
      ...prevState,
      ...patch,
    }));
  };

  const queueMenuItems = useMemo(() => {
    if (!queues.length) {
      return [
        <MenuItem key="empty" value="" disabled>
          Nenhum setor disponível
        </MenuItem>,
      ];
    }
    return queues.map((queue) => (
      <MenuItem key={queue.id} value={queue.id}>
        {queue.name}
      </MenuItem>
    ));
  }, [queues]);

  const whatsappMenuItems = useMemo(() => {
    if (!whatsapps.length) {
      return [
        <MenuItem key="empty" value="" disabled>
          Nenhuma conexão disponível
        </MenuItem>,
      ];
    }
    return whatsapps.map((whatsapp) => (
      <MenuItem key={whatsapp.id} value={whatsapp.id}>
        {whatsapp.name}
      </MenuItem>
    ));
  }, [whatsapps]);

  const jobRoleMenuItems = useMemo(
    () =>
      jobRoles.map((jobRole) => (
        <MenuItem key={jobRole.id} value={jobRole.id}>
          {jobRole.name}
        </MenuItem>
      )),
    [jobRoles]
  );

  const companyMenuItems = useMemo(
    () =>
      availableCompanies.map((company) => (
        <MenuItem key={company.id} value={company.id}>
          {company.name}
        </MenuItem>
      )),
    [availableCompanies]
  );

  const resetFormState = () => {
    setFormData(getInitialFormData());
    setImagePreview("");
    setAvailableCompanies([]);
    setUserCompanyIds([]);
    setUserMaxCompanies(1);
    setShowApiDocs(false);
    setSaving(false);
    setLoadingData(false);
    setActiveTab("dados");
    setScheduleGroups([]);
    setScheduleShowErrors(false);
  };

  const handleClose = () => {
    resetFormState();
    onClose();
  };

  useEffect(() => {
    if (!open) {
      resetFormState();
      return undefined;
    }

    let isMounted = true;

    const loadModalData = async () => {
      setLoadingData(true);

      try {
        const [queuesRes, whatsappsRes, jobRolesRes] = await Promise.all([
          api.get("/queue"),
          api.get("/whatsapp"),
          api.get("/job-roles"),
        ]);

        if (!isMounted) {
          return;
        }

        setQueues(queuesRes.data || []);
        setWhatsapps(whatsappsRes.data || []);
        setJobRoles(jobRolesRes.data || []);

        if (!selectedUser?.id) {
          setFormData(getInitialFormData());
          setImagePreview("");
          setAvailableCompanies([]);
          setUserCompanyIds([]);
          setUserMaxCompanies(1);
          setShowApiDocs(false);
          setScheduleGroups([]);
          return;
        }

        const { data } = await api.get(`/users/${selectedUser.id}`);

        if (!isMounted) {
          return;
        }

        setFormData({
          ...getInitialFormData(),
          name: data.name || "",
          email: data.email || "",
          cpf: data.cpf || "",
          profile: data.profile || "user",
          queueIds: data.queues?.map((queue) => queue.id) || [],
          whatsappId: data.whatsappId || "",
          allTicket: data.allTicket || "disabled",
          profileImage: data.profileImage || "",
          jobRoleId: data.jobRoleId || "",
          isAttendant: data.isAttendant !== undefined ? data.isAttendant : true,
          accessScope: data.accessScope || "full",
          isAI: data.isAI || false,
          aiEnabled: data.aiEnabled !== undefined ? data.aiEnabled : true,
          aiWebhookUrl: data.aiWebhookUrl || "",
          aiActions: data.aiActions || getInitialFormData().aiActions,
          aiQueueIds: data.aiQueueIds || [],
          aiAnswerNoQueue:
            data.aiAnswerNoQueue !== undefined ? data.aiAnswerNoQueue : true,
          aiWhatsappIds: data.aiWhatsappIds || [],
        });
        setScheduleGroups(scheduleValueToGroups(data.workingHours || []));
        setImagePreview(data.profileImage || "");
        setUserMaxCompanies(data.maxCompanies || 1);

        try {
          const [companiesRes, availableRes] = await Promise.all([
            api.get(`/users/${selectedUser.id}/companies`),
            api.get(`/users/${selectedUser.id}/available-companies`),
          ]);

          if (!isMounted) {
            return;
          }

          setUserCompanyIds((companiesRes.data || []).map((company) => company.id));
          setAvailableCompanies(availableRes.data || []);
        } catch (err) {
          if (!isMounted) {
            return;
          }

          setAvailableCompanies([]);
          setUserCompanyIds([]);
          toastError(err);
        }
      } catch (err) {
        if (isMounted) {
          toastError(err);
        }
      } finally {
        if (isMounted) {
          setLoadingData(false);
        }
      }
    };

    loadModalData();

    return () => {
      isMounted = false;
    };
  }, [open, selectedUser]);

  // (UX) Em conta sem assentos de Chat Interno, um usuário NOVO já abre como
  // "Apenas Atendimento" (em vez do default "Completo") — evita o admin cair no
  // erro de limite interno ao salvar. Só na criação (não força em edição).
  useEffect(() => {
    if (
      open && !selectedUser && internalLocked &&
      (formData.accessScope === "full" || formData.accessScope === "internal")
    ) {
      updateFormData({ accessScope: "attendance" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedUser, internalLocked]);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      updateFormData({ profileImage: base64String });
      setImagePreview(base64String);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveUser = async () => {
    // Bloqueia salvar com horário de trabalho inválido (faixa vazia, HH:MM
    // inválido, fim ≤ início ou sobreposição) — leva o usuário até a aba.
    if (!formData.isAI && scheduleGroupsHaveErrors(scheduleGroups)) {
      setActiveTab("horarios");
      setScheduleShowErrors(true);
      toast.error("Corrija o horário de trabalho do atendente antes de salvar.");
      return;
    }

    setSaving(true);

    try {
      // Vazio → null (sem horário próprio = usa o horário comercial da empresa).
      // IA não tem horário: nunca envia o que ficou na aba antes de trocar
      // o tipo (no POST o backend não recebe isAI e persistiria o horário).
      const workingHours = formData.isAI ? [] : scheduleGroupsToValue(scheduleGroups);
      const payload = {
        ...formData,
        jobRoleId: formData.jobRoleId || null,
        workingHours: workingHours.length ? workingHours : null,
      };

      if (selectedUser?.id) {
        await api.put(`/users/${selectedUser.id}`, payload);
        toast.success(i18n.t("users.toasts.updated"));
      } else {
        await api.post("/users", payload);
        toast.success(i18n.t("users.toasts.created"));
      }

      if (onSaved) {
        await onSaved();
      }

      handleClose();
    } catch (err) {
      toastError(err);
      setSaving(false);
    }
  };

  const handleMaxCompaniesChange = async (event) => {
    if (!selectedUser?.id) {
      return;
    }

    const value = Math.max(1, parseInt(event.target.value, 10) || 1);
    setUserMaxCompanies(value);

    try {
      await api.put(`/users/${selectedUser.id}`, { maxCompanies: value });
    } catch (err) {
      toastError(err);
    }
  };

  const handleCompaniesChange = async (event) => {
    if (!selectedUser?.id) {
      return;
    }

    const newIds = event.target.value;
    const added = newIds.filter((id) => !userCompanyIds.includes(id));
    const removed = userCompanyIds.filter((id) => !newIds.includes(id));

    try {
      await Promise.all([
        ...added.map((companyId) =>
          api.post(`/users/${selectedUser.id}/companies`, { companyId })
        ),
        ...removed.map((companyId) =>
          api.delete(`/users/${selectedUser.id}/companies/${companyId}`)
        ),
      ]);

      setUserCompanyIds(newIds);
    } catch (err) {
      toastError(err);

      try {
        const { data } = await api.get(`/users/${selectedUser.id}/companies`);
        setUserCompanyIds((data || []).map((company) => company.id));
      } catch (reloadErr) {
        toastError(reloadErr);
      }
    }
  };

  const setUserType = (isAI) => {
    updateFormData({ isAI });
    setActiveTab("dados");
  };

  const tabs = formData.isAI
    ? [
        { key: "dados", label: "Dados", icon: PersonIcon },
        { key: "comportamento", label: "Comportamento", icon: AIIcon },
      ]
    : [
        { key: "dados", label: "Dados", icon: PersonIcon },
        { key: "atendimento", label: "Atendimento", icon: Headset },
        { key: "horarios", label: "Horários", icon: Clock },
        { key: "seguranca", label: "Segurança", icon: ShieldCheck },
      ];

  return (
    <Dialog open={open} onClose={handleClose} className={classes.dialog}>
      <DialogTitle className={classes.dialogTitle}>
        {selectedUser
          ? i18n.t("userModal.title.edit")
          : i18n.t("userModal.title.add")}
      </DialogTitle>

      <DialogContent>
        {loadingData ? (
          <div className={classes.loadingState}>
            {i18n.t("userModal.loading")}
          </div>
        ) : (
          <Box mt={2}>
            {/* Tipo de usuário: Humano | IA (define isAI) */}
            <div className={classes.userTypeSelector}>
              <div
                className={`${classes.userTypeButton} ${
                  !formData.isAI ? classes.userTypeButtonActive : ""
                }`}
                onClick={() => setUserType(false)}
              >
                <PersonIcon size={22} strokeWidth={1.75} className={classes.userTypeIcon} />
                <span className={classes.userTypeLabel}>Humano</span>
              </div>
              <div
                className={`${classes.userTypeButton} ${
                  formData.isAI ? classes.userTypeButtonActive : ""
                }`}
                onClick={() => setUserType(true)}
              >
                <AIIcon size={22} strokeWidth={1.75} className={classes.userTypeIcon} />
                <span className={classes.userTypeLabel}>IA</span>
              </div>
            </div>

            {/* Abas (variam por modo) */}
            <div className={classes.tabBar} role="tablist">
              {tabs.map((tabItem) => {
                const TabIcon = tabItem.icon;
                const active = activeTab === tabItem.key;
                return (
                  <button
                    key={tabItem.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    className={`${classes.tabBtn} ${active ? classes.tabBtnActive : ""}`}
                    onClick={() => {
                      // Saindo de Horários com erro: o componente desmonta e perde o
                      // `touched`; liga showErrors para o erro não sumir ao voltar.
                      if (activeTab === "horarios" && scheduleGroupsHaveErrors(scheduleGroups)) {
                        setScheduleShowErrors(true);
                      }
                      setActiveTab(tabItem.key);
                    }}
                  >
                    <TabIcon size={16} strokeWidth={active ? 1.9 : 1.75} />
                    <span className={classes.tabLabelXs}>{tabItem.label}</span>
                  </button>
                );
              })}
            </div>

            {/* Input de upload (sempre montado; usado pelos avatares das abas Dados) */}
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: "none" }}
              id="user-editor-profile-image-upload"
            />

            <div
              className={classes.tabPanel}
              key={`${formData.isAI ? "ai" : "human"}-${activeTab}`}
            >
              {/* ───────── HUMANO · DADOS ───────── */}
              {!formData.isAI && activeTab === "dados" && (
                <>
                  <div className={classes.identityRow}>
                    <label
                      htmlFor="user-editor-profile-image-upload"
                      className={classes.avatarUpload}
                    >
                      <Avatar className={classes.identityAvatar} src={imagePreview || undefined} alt="">
                        {(formData.name || "?").trim().charAt(0).toUpperCase()}
                      </Avatar>
                      <span className={classes.avatarOverlay}>
                        <Upload size={16} strokeWidth={2} />
                        {imagePreview ? "Alterar" : "Foto"}
                      </span>
                    </label>
                    <div className={classes.identityFields}>
                      <FormControl className={classes.formControlTight}>
                        <TextField
                          label={i18n.t("userModal.form.name")}
                          variant="outlined"
                          value={formData.name}
                          onChange={(event) => updateFormData({ name: event.target.value })}
                          fullWidth
                          className={classes.formField}
                        />
                      </FormControl>
                      <span className={classes.identityHint}>
                        Clique no avatar para enviar uma foto (opcional).
                      </span>
                    </div>
                  </div>

                  <div className={classes.row2}>
                    <FormControl className={classes.formControlTight}>
                      <TextField
                        label={i18n.t("userModal.form.email")}
                        variant="outlined"
                        type="email"
                        value={formData.email}
                        onChange={(event) => updateFormData({ email: event.target.value })}
                        fullWidth
                        className={classes.formField}
                      />
                    </FormControl>
                    <FormControl className={classes.formControlTight}>
                      <InputMask
                        mask="999.999.999-99"
                        maskChar={null}
                        value={formData.cpf}
                        onChange={(event) => updateFormData({ cpf: event.target.value })}
                      >
                        {(inputProps) => (
                          <TextField
                            {...inputProps}
                            label={i18n.t("userModal.form.cpf")}
                            variant="outlined"
                            fullWidth
                            className={classes.formField}
                          />
                        )}
                      </InputMask>
                    </FormControl>
                  </div>

                  <div className={classes.row2}>
                    <FormControl className={classes.formControlTight}>
                      <TextField
                        label={i18n.t("userModal.form.password")}
                        variant="outlined"
                        type="password"
                        value={formData.password}
                        onChange={(event) => updateFormData({ password: event.target.value })}
                        fullWidth
                        className={classes.formField}
                      />
                    </FormControl>
                    <ModernSelect
                      label={i18n.t("userModal.form.profile")}
                      value={formData.profile}
                      onChange={(event) => updateFormData({ profile: event.target.value })}
                      MenuProps={MENU_PROPS}
                      formControlProps={{
                        className: `${classes.formControlTight} ${classes.formField}`,
                      }}
                    >
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="user">User</MenuItem>
                    </ModernSelect>
                  </div>
                </>
              )}

              {/* ───────── HUMANO · ATENDIMENTO ───────── */}
              {!formData.isAI && activeTab === "atendimento" && (
                <>
                  <div className={classes.row2}>
                    <ModernSelect
                      label={i18n.t("userModal.form.whatsapp")}
                      value={formData.whatsappId}
                      onChange={(event) => updateFormData({ whatsappId: event.target.value })}
                      displayEmpty
                      MenuProps={MENU_PROPS}
                      formControlProps={{
                        className: `${classes.formControlTight} ${classes.formField}`,
                      }}
                    >
                      <MenuItem value="">{i18n.t("userModal.form.noWhatsapp")}</MenuItem>
                      {whatsappMenuItems}
                    </ModernSelect>
                    <ModernSelect
                      label={i18n.t("userModal.form.jobRole")}
                      value={formData.jobRoleId}
                      onChange={(event) => updateFormData({ jobRoleId: event.target.value })}
                      displayEmpty
                      MenuProps={MENU_PROPS}
                      formControlProps={{
                        className: `${classes.formControlTight} ${classes.formField}`,
                      }}
                    >
                      <MenuItem value="">{i18n.t("userModal.form.noJobRole")}</MenuItem>
                      {jobRoleMenuItems}
                    </ModernSelect>
                  </div>

                  <div className={classes.pickerField}>
                    <span className={classes.pickerLabel}>
                      {i18n.t("userModal.form.queues")}
                    </span>
                    <QueueMultiSelectPopover
                      title={i18n.t("userModal.form.queues")}
                      placeholder="Selecione os setores"
                      searchPlaceholder="Pesquisar setores"
                      allLabel="Todas as filas"
                      emptyLabel="Nenhum setor selecionado"
                      selectedLabel="setor(es)"
                      clearLabel="Limpar"
                      noQueuesFoundLabel="Nenhum setor encontrado"
                      queues={queues}
                      value={formData.queueIds}
                      onChange={(ids) => updateFormData({ queueIds: ids })}
                      fullWidth
                    />
                  </div>

                  <div className={classes.row2}>
                    <ModernSelect
                      label={i18n.t("userModal.form.operationalType")}
                      value={formData.isAttendant ? "atendente" : "supervisor"}
                      onChange={(event) =>
                        updateFormData({
                          isAttendant: event.target.value === "atendente",
                        })
                      }
                      MenuProps={MENU_PROPS}
                      formControlProps={{
                        className: `${classes.formControlTight} ${classes.formField}`,
                      }}
                    >
                      <MenuItem value="atendente">
                        {i18n.t("userModal.form.attendant")}
                      </MenuItem>
                      <MenuItem value="supervisor">
                        {i18n.t("userModal.form.supervisor")}
                      </MenuItem>
                    </ModernSelect>
                    <ModernSelect
                      label={i18n.t("userModal.form.accessScope")}
                      value={formData.accessScope || "full"}
                      onChange={(event) => updateFormData({ accessScope: event.target.value })}
                      MenuProps={MENU_PROPS}
                      formControlProps={{
                        className: `${classes.formControlTight} ${classes.formField}`,
                      }}
                    >
                      <MenuItem value="full" disabled={internalLocked}>
                        {i18n.t("userModal.form.accessScopeFull")}
                      </MenuItem>
                      <MenuItem value="attendance">
                        {i18n.t("userModal.form.accessScopeAttendance")}
                      </MenuItem>
                      <MenuItem value="internal" disabled={internalLocked}>
                        {i18n.t("userModal.form.accessScopeInternal")}
                      </MenuItem>
                    </ModernSelect>
                  </div>
                  {internalLocked && (
                    <Typography style={{ fontSize: 12, lineHeight: 1.4, color: "#f59e0b", marginTop: -4 }}>
                      {i18n.t("userModal.form.internalLockedNotice")}
                    </Typography>
                  )}
                </>
              )}

              {/* ───────── HUMANO · HORÁRIOS ───────── */}
              {!formData.isAI && activeTab === "horarios" && (
                <UserScheduleField
                  groups={scheduleGroups}
                  onChange={setScheduleGroups}
                  showErrors={scheduleShowErrors}
                />
              )}

              {/* ───────── HUMANO · SEGURANÇA ───────── */}
              {!formData.isAI && activeTab === "seguranca" && (
                <>
                  {!selectedUser && (
                    <div className={classes.hintCard}>
                      <Info size={18} strokeWidth={1.85} style={{ flexShrink: 0, marginTop: 1 }} />
                      <span>
                        Recursos de segurança (autenticação em duas etapas e acesso
                        multi-empresa) ficam disponíveis após salvar o usuário.
                      </span>
                    </div>
                  )}

                  {selectedUser && (loggedInUser?.super || loggedInUser?.profile === "admin") && (
                    <Box className={classes.mfaCard} style={{ marginTop: 0 }}>
                      <div className={classes.mfaCardLeft}>
                        <div
                          className={`${classes.mfaIconBadge} ${
                            selectedUser.mfaEnabled
                              ? classes.mfaIconBadgeOn
                              : classes.mfaIconBadgeOff
                          }`}
                        >
                          {selectedUser.mfaEnabled ? (
                            <ShieldCheck size={20} strokeWidth={1.75} />
                          ) : (
                            <ShieldOff size={20} strokeWidth={1.75} />
                          )}
                        </div>
                        <div className={classes.mfaTextWrap}>
                          <span className={classes.mfaTitle}>
                            {i18n.t("mfa.profile.menuItem")}
                          </span>
                          <span
                            className={`${classes.mfaStatus} ${
                              selectedUser.mfaEnabled
                                ? classes.mfaStatusOn
                                : classes.mfaStatusOff
                            }`}
                          >
                            {selectedUser.mfaEnabled
                              ? selectedUser.mfaEnabledAt
                                ? i18n.t("mfa.profile.statusEnabled", {
                                    date: new Date(selectedUser.mfaEnabledAt).toLocaleDateString(),
                                  })
                                : i18n.t("mfa.profile.statusEnabled", { date: "—" })
                              : i18n.t("mfa.profile.statusDisabled")}
                          </span>
                        </div>
                      </div>
                      {selectedUser.mfaEnabled && (
                        <Button
                          size="small"
                          variant="outlined"
                          className={classes.mfaResetBtn}
                          startIcon={<RotateCcw size={14} strokeWidth={2} />}
                          onClick={async () => {
                            if (!window.confirm(i18n.t("mfa.profile.adminResetConfirm"))) return;
                            try {
                              await api.post(`/users/${selectedUser.id}/mfa/reset`);
                              toast.success(i18n.t("mfa.profile.adminResetDone"));
                              if (onSaved) onSaved();
                            } catch (err) {
                              toastError(err);
                            }
                          }}
                        >
                          {i18n.t("mfa.profile.adminReset")}
                        </Button>
                      )}
                    </Box>
                  )}

                  {selectedUser && loggedInUser?.super && (
                    <FormControl className={classes.formControl} style={{ marginTop: 0 }}>
                      <TextField
                        label="Máx. empresas"
                        type="number"
                        variant="outlined"
                        className={classes.formField}
                        inputProps={{ min: 1, max: 50 }}
                        value={userMaxCompanies}
                        onChange={handleMaxCompaniesChange}
                        helperText="Quantas empresas este usuário pode atender"
                      />
                    </FormControl>
                  )}

                  {selectedUser && availableCompanies.length > 0 && (
                    <ModernSelect
                      label="Empresas do Grupo"
                      multiple
                      value={userCompanyIds}
                      onChange={handleCompaniesChange}
                      renderValue={(selected) =>
                        availableCompanies
                          .filter((company) => selected.includes(company.id))
                          .map((company) => company.name)
                          .join(", ")
                      }
                      MenuProps={MENU_PROPS}
                      formControlProps={{
                        className: `${classes.formControl} ${classes.formField}`,
                      }}
                    >
                      {companyMenuItems}
                    </ModernSelect>
                  )}
                </>
              )}

              {/* ───────── IA · DADOS ───────── */}
              {formData.isAI && activeTab === "dados" && (
                <>
                  <div className={classes.identityRow}>
                    <label
                      htmlFor="user-editor-profile-image-upload"
                      className={classes.avatarUpload}
                    >
                      <Avatar className={classes.identityAvatar} src={imagePreview || undefined} alt="">
                        {(formData.name || "IA").trim().charAt(0).toUpperCase()}
                      </Avatar>
                      <span className={classes.avatarOverlay}>
                        <Upload size={16} strokeWidth={2} />
                        {imagePreview ? "Alterar" : "Foto"}
                      </span>
                    </label>
                    <div className={classes.identityFields}>
                      <FormControl className={classes.formControlTight}>
                        <TextField
                          label="Nome da IA"
                          variant="outlined"
                          value={formData.name}
                          onChange={(event) => updateFormData({ name: event.target.value })}
                          fullWidth
                          className={classes.formField}
                        />
                      </FormControl>
                      <span className={classes.identityHint}>
                        Avatar do agente de IA (opcional).
                      </span>
                    </div>
                  </div>

                  <div className={classes.switchRow}>
                    <span className={classes.switchRowLabel}>
                      <span className={classes.gradientText}>IA Ativa</span>
                    </span>
                    <Switch
                      checked={formData.aiEnabled}
                      onChange={(event) => updateFormData({ aiEnabled: event.target.checked })}
                      color="primary"
                    />
                  </div>
                </>
              )}

              {/* ───────── IA · COMPORTAMENTO ───────── */}
              {formData.isAI && activeTab === "comportamento" && (
                <>
                  <FormControl className={classes.formControl}>
                    <TextField
                      label="URL do Webhook"
                      variant="outlined"
                      value={formData.aiWebhookUrl}
                      onChange={(event) => updateFormData({ aiWebhookUrl: event.target.value })}
                      fullWidth
                      className={classes.formField}
                      placeholder="https://seu-n8n.com/webhook/..."
                      helperText="Endpoint que receberá as mensagens"
                    />
                  </FormControl>

                  <div className={classes.row2}>
                    <ModernSelect
                      label="Filas"
                      multiple
                      value={formData.aiQueueIds}
                      onChange={(event) => updateFormData({ aiQueueIds: event.target.value })}
                      renderValue={(selected) =>
                        selected.length === 0
                          ? "Todas"
                          : queues
                              .filter((queue) => selected.includes(queue.id))
                              .map((queue) => queue.name)
                              .join(", ")
                      }
                      MenuProps={MENU_PROPS}
                      formControlProps={{
                        className: `${classes.formControlTight} ${classes.formField}`,
                      }}
                    >
                      {queueMenuItems}
                    </ModernSelect>
                    <ModernSelect
                      label="Conexões"
                      multiple
                      value={formData.aiWhatsappIds}
                      onChange={(event) => updateFormData({ aiWhatsappIds: event.target.value })}
                      renderValue={(selected) =>
                        selected.length === 0
                          ? "Todas"
                          : whatsapps
                              .filter((whatsapp) => selected.includes(whatsapp.id))
                              .map((whatsapp) => whatsapp.name)
                              .join(", ")
                      }
                      MenuProps={MENU_PROPS}
                      formControlProps={{
                        className: `${classes.formControlTight} ${classes.formField}`,
                      }}
                    >
                      {whatsappMenuItems}
                    </ModernSelect>
                  </div>

                  <div className={classes.switchRow}>
                    <span className={classes.switchRowLabel}>Responder tickets sem fila</span>
                    <Switch
                      checked={formData.aiAnswerNoQueue}
                      onChange={(event) => updateFormData({ aiAnswerNoQueue: event.target.checked })}
                      color="primary"
                    />
                  </div>

                  <div className={classes.docSection}>
                    <div
                      className={classes.docTitle}
                      onClick={() => setShowApiDocs((prevState) => !prevState)}
                    >
                      <ChevronDown
                        size={18}
                        strokeWidth={1.75}
                        style={{
                          transform: showApiDocs ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.3s ease",
                        }}
                      />
                      Formato de Resposta da IA
                    </div>
                    <Collapse in={showApiDocs}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ display: "block", marginBottom: 12 }}
                      >
                        <strong>O webhook recebe (POST):</strong>
                      </Typography>
                      <div className={classes.codeBlock}>
                        <pre>{`{
  "ticketId": 123,
  "contactName": "João Silva",
  "contactNumber": "5511999999999",
  "message": "Texto da mensagem do cliente",
  "aiUserId": 5
}`}</pre>
                      </div>

                      <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ display: "block", marginBottom: 8, marginTop: 16 }}
                      >
                        <strong>Resposta esperada (JSON):</strong>
                      </Typography>
                      <div className={classes.codeBlock}>
                        <pre>{`// Enviar mensagem
{ "action": "sendMessage", "message": "Olá! Como posso ajudar?" }

// Transferir para usuário (ID do usuário)
{ "action": "transferUser", "userId": 5, "message": "Transferindo..." }

// Transferir para fila (ID da fila)
{ "action": "transferQueue", "queueId": 2, "message": "Encaminhando..." }

// Resolver/Fechar ticket
{ "action": "resolveTicket", "message": "Atendimento finalizado!" }`}</pre>
                      </div>
                    </Collapse>
                  </div>
                </>
              )}
            </div>
          </Box>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={handleClose}
          disabled={saving}
          className={`${classes.btnBase} ${classes.btnCancel}`}
          startIcon={<X size={16} strokeWidth={1.75} />}
        >
          {i18n.t("userModal.buttons.cancel")}
        </Button>
        <Button
          onClick={handleSaveUser}
          disabled={saving || loadingData}
          className={`${classes.btnBase} ${classes.btnPrimary}`}
          startIcon={
            saving ? (
              <CircularProgress size={14} className={classes.spinner} />
            ) : selectedUser ? (
              <Pencil size={16} strokeWidth={1.75} />
            ) : (
              <Plus size={16} strokeWidth={1.75} />
            )
          }
        >
          {selectedUser
            ? i18n.t("userModal.buttons.okEdit")
            : i18n.t("userModal.buttons.okAdd")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserEditorModal;
